import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import {
  ALL_WIDGETS,
  CATEGORY_LABEL_KEYS,
  TYPE_LABEL_KEYS,
  WIDGET_REGISTRY,
  WidgetCategory,
  WidgetType,
  WidgetDefinition
} from "@/config/dashboardWidgets";
import { useDashboardConfigStore } from "@/store/dashboardConfigStore";
import { useUserStore } from "@/store/userStore";
import { RotateCcw, Save, X, Loader2, Info } from "lucide-react";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface DashboardConfiguratorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// Group widgets by type
function groupWidgetsByType(widgets: WidgetDefinition[]): Record<WidgetType, WidgetDefinition[]> {
  return widgets.reduce((acc, widget) => {
    if (!acc[widget.type]) {
      acc[widget.type] = [];
    }
    acc[widget.type].push(widget);
    return acc;
  }, {} as Record<WidgetType, WidgetDefinition[]>);
}

export function DashboardConfigurator({ open, onOpenChange }: DashboardConfiguratorProps) {
  const { t } = useTranslation("adminui2");
  const { toast } = useToast();
  const { gymId } = useUserStore();
  const { 
    config, 
    toggleWidget, 
    resetToDefault, 
    setEditMode,
    isWidgetVisible,
    saveConfig,
    isSaving,
    configSource,
  } = useDashboardConfigStore();

  const widgetsByType = groupWidgetsByType(ALL_WIDGETS);
  
  const handleSave = async () => {
    if (!gymId) {
      toast({
        title: t("configurator.errorTitle"),
        description: t("configurator.errorNoGym"),
        variant: "destructive",
      });
      return;
    }

    const success = await saveConfig(gymId);
    
    if (success) {
      setEditMode(false);
      onOpenChange(false);
      toast({
        title: t("configurator.savedTitle"),
        description: t("configurator.savedDesc"),
      });
    } else {
      toast({
        title: t("configurator.errorTitle"),
        description: t("configurator.errorSaveFailed"),
        variant: "destructive",
      });
    }
  };

  const handleReset = () => {
    resetToDefault();
    toast({
      title: t("configurator.resetTitle"),
      description: t("configurator.resetDesc"),
    });
  };

  // Le compte doit passer par le registre, comme partout ailleurs : une
  // configuration enregistrée avant le retrait des KPI factices garde leurs
  // entrées `visible: true`, et le badge annonçait alors plus de widgets actifs
  // que la liste n'en coche et que le tableau de bord n'en rend.
  const visibleCount = config.widgets.filter(w => w.visible && WIDGET_REGISTRY[w.id]).length;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-lg">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            {t("configurator.title")}
          </SheetTitle>
          <SheetDescription>
            {t("configurator.description")}
            <Badge variant="secondary" className="ml-2">
              {t("configurator.activeCount", { count: visibleCount })}
            </Badge>
          </SheetDescription>
        </SheetHeader>

        {/* Fallback config indicator */}
        {configSource === 'fallback_gym' && (
          <Alert className="mt-4 bg-amber-50 border-amber-200 dark:bg-amber-950/30 dark:border-amber-800">
            <Info className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            <AlertDescription className="text-amber-700 dark:text-amber-300 text-sm">
              {t("configurator.fallbackConfig")}
            </AlertDescription>
          </Alert>
        )}

        {configSource === 'default' && (
          <Alert className="mt-4 bg-blue-50 border-blue-200 dark:bg-blue-950/30 dark:border-blue-800">
            <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            <AlertDescription className="text-blue-700 dark:text-blue-300 text-sm">
              {t("configurator.defaultConfig")}
            </AlertDescription>
          </Alert>
        )}

        <ScrollArea className="h-[calc(100vh-260px)] mt-6 pr-4">
          <div className="space-y-6">
            {/* Render widgets by type */}
            {Object.entries(widgetsByType).map(([type, widgets]) => (
              <div key={type}>
                <h3 className="font-semibold text-sm mb-3 flex items-center gap-2">
                  {t(TYPE_LABEL_KEYS[type as WidgetType])}
                  <Badge variant="outline" className="text-xs">
                    {widgets.filter(w => isWidgetVisible(w.id)).length}/{widgets.length}
                  </Badge>
                </h3>
                <div className="space-y-2">
                  {widgets.map((widget) => {
                    const Icon = widget.icon;
                    const isVisible = isWidgetVisible(widget.id);
                    
                    return (
                      <div
                        key={widget.id}
                        className={cn(
                          "flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors",
                          isVisible 
                            ? "bg-primary/5 border-primary/20" 
                            : "bg-muted/30 border-transparent hover:bg-muted/50"
                        )}
                        onClick={() => toggleWidget(widget.id)}
                      >
                        <Checkbox 
                          checked={isVisible}
                          onCheckedChange={() => toggleWidget(widget.id)}
                          className="pointer-events-none"
                        />
                        <div className={cn(
                          "p-1.5 rounded-md",
                          isVisible ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"
                        )}>
                          <Icon className="h-4 w-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{t(widget.nameKey)}</p>
                          <p className="text-xs text-muted-foreground truncate">
                            {t(widget.descriptionKey)}
                          </p>
                        </div>
                        <Badge variant="outline" className="text-xs shrink-0">
                          {t(CATEGORY_LABEL_KEYS[widget.category])}
                        </Badge>
                      </div>
                    );
                  })}
                </div>
                <Separator className="mt-4" />
              </div>
            ))}
          </div>
        </ScrollArea>

        {/* Footer actions */}
        <div className="absolute bottom-0 left-0 right-0 p-4 bg-background border-t flex gap-2">
          <Button
            variant="outline"
            onClick={handleReset}
            className="flex-1"
            disabled={isSaving}
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            {t("configurator.reset")}
          </Button>
          <Button
            onClick={handleSave}
            className="flex-1"
            disabled={isSaving}
          >
            {isSaving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            {t("configurator.save")}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
